import {
    ICountryZone,
    IMerchantFilteringExchangeLocation,
    REST_OF_WORLD,
} from '../../../../../models/User';
import {isSet} from '../../../../../models/model-types-methods';
import {ICheckboxesTreeItem} from '../../../../../components/inputs/checkboxes-tree-dropdown/checkboxes-tree/stores/checkboxes-tree-store';
import {ZONE_TYPE} from './exchange-locations-table-builder';

/**
 * Stateless utility for converting between zone data and tree structures for CheckboxesTreeDropdown.
 * Responsibilities:
 * - Build flat tree of countries
 * - Convert locations to checked tree structure
 * - Extract locations from tree combined with warehouse IDs (cross-product)
 * - Provide O(1) lookups for human-readable labels
 */
class ZonesTreeConverter {

    private readonly _optionsTree: ICheckboxesTreeItem[] = [];
    private readonly _labelByCodeMap: Map<string, string> = new Map();

    /**
     * Initialize converter with countries.
     * Builds and caches flat options tree and label lookup map.
     */
    constructor(private readonly _countries: ICountryZone[]) {
        this._labelByCodeMap = this._buildLabelByCodeMap();
        this._optionsTree = this._buildOptionsTree();
    }

    /**
     * Get cached flat options tree for CheckboxesTreeDropdown.
     * Returns flat tree structure of countries.
     */
    public getOptionsTree(): ICheckboxesTreeItem[] {
        return this._optionsTree;
    }

    /**
     * Convert locations to checked tree structure for CheckboxesTreeDropdown.
     * Returns undefined for no selection, [] for select all, or tree for specific selection.
     */
    public buildValueTree(
        locations: IMerchantFilteringExchangeLocation[]
    ): ICheckboxesTreeItem[] | undefined {
        if (!isSet(locations) || locations.length === 0) {
            return undefined;
        }

        return this._buildSelectedTreeFromLocations(locations);
    }

    /**
     * Extract locations from zones tree combined with warehouse IDs (warehouse × zone cross-product).
     * Converts CheckboxesTreeDropdown selections into IMerchantFilteringExchangeLocation[] array.
     * Each warehouse is paired with each selected country.
     * Used in ADD and EDIT modes to create location rules.
     * Note: setting_id and id are not set (backend assigns them on save).
     * Example: 2 warehouses × 3 countries = 6 locations.
     */
    public extractLocations(
        tree: ICheckboxesTreeItem[] | undefined,
        warehouseIds: string[]
    ): IMerchantFilteringExchangeLocation[] {
        if (warehouseIds.length === 0 || !isSet(tree) || tree.length === 0) {
            return [];
        }

        /** Extract country codes from tree */
        const countryCodes = this._extractCountryCodesFromTree(tree);

        /** Build cross-product: each warehouse × each country */
        const locations: IMerchantFilteringExchangeLocation[] = [];
        warehouseIds.forEach((warehouseId) => {
            countryCodes.forEach((countryCode) => {
                locations.push({
                    location_id: warehouseId,
                    predefined_country_group: null,
                    country_code: countryCode,
                });
            });
        });

        return locations;
    }

    /** Get human-readable label for zone code (O(1) lookup) */
    private resolveLabelByCode(code: string): string {
        return this._labelByCodeMap.get(code) ?? code;
    }

    /**
     * Build label lookup map for O(1) access.
     * Format: code → label
     * Example: "US" → "United States"
     */
    private _buildLabelByCodeMap(): Map<string, string> {
        const labelMap = new Map<string, string>();

        this._countries.forEach((country) => {
            labelMap.set(country.code, country.label);
        });

        return labelMap;
    }

    /**
     * Build flat options tree of countries.
     */
    private _buildOptionsTree(): ICheckboxesTreeItem[] {
        return this._countries.map((country) => {
            return this._createTreeNode(
                country.code,
                country.label,
                ZONE_TYPE.COUNTRY
            );
        });
    }

    /** Build a selected tree from locations by creating a minimal node structure */
    private _buildSelectedTreeFromLocations(
        locations: IMerchantFilteringExchangeLocation[]
    ): ICheckboxesTreeItem[] {
        const selectedRoots: ICheckboxesTreeItem[] = [];

        locations.forEach((location) => {
            this._addLocationToTree(selectedRoots, location);
        });

        return selectedRoots;
    }

    /** Add location to tree by finding or creating nodes */
    private _addLocationToTree(
        roots: ICheckboxesTreeItem[],
        location: IMerchantFilteringExchangeLocation
    ): void {

        /** Case 1: Predefined country group (only REST_OF_WORLD is supported) */
        if (location.predefined_country_group === REST_OF_WORLD) {
            /** No tree representation for REST_OF_WORLD in zones dropdown */
            return;
        }

        /** Case 2: Specific country */
        if (location.country_code) {
            this._findOrCreateNode(
                roots,
                location.country_code,
                ZONE_TYPE.COUNTRY
            );
        }
    }

    /**
     * Extract country codes from CheckboxesTreeDropdown tree.
     * Returns array of country codes for cross-product with warehouses.
     */
    private _extractCountryCodesFromTree(tree: ICheckboxesTreeItem[]): string[] {
        return tree.map((node) => node.value);
    }

    /** Find existing node or create new one in parent array */
    private _findOrCreateNode(
        parent: ICheckboxesTreeItem[],
        value: string,
        type: ZONE_TYPE
    ): ICheckboxesTreeItem {
        let node = parent.find((item) => item.value === value);

        if (!node) {
            const label = this.resolveLabelByCode(value);
            node = this._createTreeNode(value, label, type);
            parent.push(node);
        }

        return node;
    }

    /** Create a tree node */
    private _createTreeNode(
        value: string,
        label: string,
        type: ZONE_TYPE
    ): ICheckboxesTreeItem {
        return {
            value,
            label,
            type,
            disabled: false,
        };
    }
}

export default ZonesTreeConverter;

